
import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, onSnapshot, addDoc, query, where, doc, deleteDoc } from 'firebase/firestore';
import { UserRole } from '../types';

interface SubjectManagementProps {
  user: any;
  onSubjectClick?: (subjectId: string) => void;
  onBack?: () => void;
}

const SubjectManagement: React.FC<SubjectManagementProps> = ({ user, onSubjectClick, onBack }) => {
  const [subjects, setSubjects] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [name, setName] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');

  useEffect(() => {
    const unsubscribeClasses = onSnapshot(collection(db, 'classes'), (snap) => {
      setClasses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    
    let q;
    if (user.role === UserRole.STUDENT) {
        if (user.classId) {
            q = query(collection(db, 'subjects'), where('classId', '==', user.classId));
        } else {
            setSubjects([]);
            return unsubscribeClasses;
        }
    } else if (user.role === UserRole.STAFF) {
        q = query(collection(db, 'subjects'), where('staffId', '==', user.uid));
    } else {
        q = collection(db, 'subjects');
    }

    const unsubscribeSubjects = onSnapshot(q, (snap) => {
      setSubjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => {
        unsubscribeClasses();
        unsubscribeSubjects();
    };
  }, [user.uid, user.role, user.classId]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClassId) return alert("Please select a class first");
    await addDoc(collection(db, 'subjects'), { 
        name, 
        classId: selectedClassId, 
        staffId: user.uid,
        staffName: user.displayName,
        createdAt: Date.now()
    });
    setName('');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
            {user.role === UserRole.STUDENT && (
                <button onClick={onBack} className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 hover:bg-purple-600 hover:text-white transition-all">
                    <i className="fas fa-arrow-left"></i>
                </button>
            )}
            <h2 className="text-2xl font-bold dark:text-white">Academic Subjects</h2>
        </div>
        {user.role === UserRole.STUDENT && (
            <div className="px-4 py-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-xl text-xs font-bold border border-purple-100 dark:border-purple-800">
                Current Class: {classes.find(c => c.id === user.classId)?.name || 'Unassigned'}
            </div>
        )}
      </div>

      {user.role !== UserRole.STUDENT && (
        <form onSubmit={handleAdd} className="bg-white dark:bg-dark-card p-6 rounded-2xl border border-gray-100 dark:border-gray-800 grid grid-cols-1 md:grid-cols-3 gap-4 shadow-sm">
          <select required className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl dark:text-white outline-none border-none" value={selectedClassId} onChange={e => setSelectedClassId(e.target.value)}>
            <option value="">Choose Class</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input required placeholder="Subject Name (e.g. Mathematics)" className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl dark:text-white outline-none border-none" value={name} onChange={e => setName(e.target.value)} />
          <button type="submit" className="bg-purple-600 text-white font-bold rounded-xl shadow-lg hover:bg-purple-700 transition-all">Create Subject</button>
        </form>
      )}
      
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {subjects.map(s => (
          <div 
            key={s.id} 
            onClick={() => user.role === UserRole.STUDENT && onSubjectClick?.(s.id)}
            className={`bg-white dark:bg-dark-card p-6 rounded-2xl border border-gray-100 dark:border-gray-800 flex justify-between items-center shadow-sm group hover:border-purple-300 transition-all ${user.role === UserRole.STUDENT ? 'cursor-pointer hover:shadow-lg' : ''}`}
          >
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                Class: {classes.find(c => c.id === s.classId)?.name || 'Academic Room'}
              </p>
              <h4 className="font-bold dark:text-white text-lg">{s.name}</h4>
              <p className="text-xs text-purple-500 font-medium">Instructor: {s.staffName}</p>
            </div>
            { (user.role === UserRole.ADMIN || user.uid === s.staffId) && (
              <button onClick={(e) => { e.stopPropagation(); deleteDoc(doc(db, 'subjects', s.id)); }} className="text-red-400 p-2 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="fas fa-trash"></i>
              </button>
            )}
            {user.role === UserRole.STUDENT && (
                <div className="w-8 h-8 rounded-full bg-purple-50 dark:bg-gray-800 flex items-center justify-center text-purple-600 group-hover:bg-purple-600 group-hover:text-white transition-all">
                    <i className="fas fa-chevron-right text-xs"></i>
                </div>
            )}
          </div>
        ))}
      </div>

      {user.role === UserRole.STUDENT && subjects.length === 0 && (
          <div className="text-center py-20 bg-gray-50 dark:bg-gray-800/20 rounded-3xl border-2 border-dashed border-gray-200 dark:border-gray-800">
              <i className="fas fa-book-open text-4xl text-gray-300 mb-4"></i>
              <p className="text-gray-400 font-medium">No subjects assigned to your class yet.</p>
          </div>
      )}
    </div>
  );
};

export default SubjectManagement;
